import { bytes, since, took } from "../board.js";
import { Bar, Brand, Calm, Certificates, Clock, DeployCard, Feed, IncidentCard, Label, Orb, ProjectRow, TONE, headlineOf, toneOf } from "../parts.jsx";

/**
 * Layout 2 — Stage.
 *
 * One thing at a time, large. The middle of the screen is a stage that shows
 * whatever matters most this minute: a deployment while one runs, the
 * incidents while any are open, and otherwise the box breathing quietly with
 * its last deployment beside it. The fleet is a list on the left, what has
 * happened is a feed on the right, and the box's resources are a strip along
 * the bottom.
 */
export function Stage({ board, projects, feed, screenKey, now }) {
    const headline = headlineOf(board);
    const h = toneOf(headline.tone);
    const { counts } = board.status;
    const active = board.deployments.active;
    const open = board.incidents.open;
    const { box } = board;
    const last = board.deployments.recent[0];

    return (
        <div className="grid h-full grid-rows-[auto_minmax(0,1fr)_auto] gap-[1.2rem]">
            <header className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
                <Brand sub="stage" />
                <div className="flex items-center gap-4">
                    <Orb tone={headline.tone} size="2.4rem" />
                    <div className={`text-[1.6rem] font-semibold leading-none tracking-[0.08em] ${h.text}`}>{headline.text}</div>
                </div>
                <Clock />
            </header>

            <section className="grid min-h-0 grid-cols-1 gap-[1.2rem] lg:grid-cols-[1fr_2.6fr_1.15fr]">
                <aside className="live-card flex min-h-0 flex-col p-[1rem]">
                    <div className="flex items-baseline justify-between">
                        <Label>fleet</Label>
                        <span className="live-num text-[0.8rem] text-gray-600">{counts.up}/{counts.total}</span>
                    </div>
                    <div className="mt-2 flex min-h-0 flex-1 flex-col overflow-hidden">
                        {[...projects.values()].map((project) => (
                            <ProjectRow key={project.slug} project={project} screenKey={screenKey} now={now} />
                        ))}
                    </div>
                </aside>

                <main className="flex min-h-0 flex-col gap-[1rem] overflow-hidden">
                    {active.length ? (
                        <>
                            <DeployCard deployment={active[0]} project={projects.get(active[0].slug)} screenKey={screenKey} big siblings={active} />
                            {active.length > 1 ? (
                                <div className="flex flex-wrap gap-2">
                                    {active.slice(1).map((deployment) => (
                                        <span key={deployment.id} className="live-card px-3 py-1.5 text-[0.85rem] text-gray-300">
                                            <span className="font-medium text-gray-100">{deployment.slug}</span> <span className="live-num text-gray-500">#{deployment.number}</span> · {deployment.status}
                                        </span>
                                    ))}
                                </div>
                            ) : null}
                            {open.length ? (
                                <div className="flex min-h-0 flex-col gap-[0.8rem] overflow-hidden">
                                    {open.slice(0, 2).map((incident) => <IncidentCard key={incident.id} incident={incident} />)}
                                </div>
                            ) : null}
                        </>
                    ) : open.length ? (
                        <div className={`live-card flex min-h-0 flex-1 flex-col gap-[1rem] p-[1.6rem] ${TONE.down.border} bg-red-500/[0.06]`}>
                            <div className="flex items-center gap-4">
                                <Orb tone="down" size="3rem" />
                                <div>
                                    <div className="text-[2.6rem] font-semibold leading-none tracking-[0.06em] text-red-400">{open.length} NOT ANSWERING</div>
                                    <div className="live-label mt-2">since {since(open.at(-1).startedAt, now)} · {counts.up} of {counts.total} still answering</div>
                                </div>
                            </div>
                            <div className="flex min-h-0 flex-1 flex-col gap-[0.8rem] overflow-hidden">
                                {open.map((incident) => <IncidentCard key={incident.id} incident={incident} big />)}
                            </div>
                        </div>
                    ) : (
                        <Idle board={board} counts={counts} last={last} projects={projects} screenKey={screenKey} now={now} />
                    )}
                </main>

                <aside className="live-card flex min-h-0 flex-col p-[1rem]">
                    <Label>activity</Label>
                    <div className="mt-2 min-h-0 flex-1 overflow-hidden">
                        <Feed events={feed} limit={10} projects={projects} screenKey={screenKey} />
                    </div>
                    <Certificates certificates={board.certificates} />
                </aside>
            </section>

            <footer className="live-card grid grid-cols-2 items-center gap-x-[2rem] gap-y-[1rem] p-[1rem] md:grid-cols-5">
                <Bar label="cpu" ratio={(box.cpu ?? 0) / 100} text={box.cpu === null ? "—" : `${Math.round(box.cpu)}%`} />
                <Bar label="memory" ratio={box.memory.total ? box.memory.used / box.memory.total : 0} text={bytes(box.memory.used)} />
                <Bar label="disk" ratio={box.disk.total ? box.disk.used / box.disk.total : 0} text={box.disk.total ? `${Math.round((box.disk.used / box.disk.total) * 100)}%` : "—"} />
                <div>
                    <Label>processes</Label>
                    <div className="live-num mt-1 text-[1.3rem] font-light leading-none">
                        <span className={box.processes.stopped ? "text-amber-400" : "text-gray-100"}>{box.processes.online}</span>
                        <span className="text-gray-600">/{box.processes.total}</span>
                    </div>
                </div>
                <div>
                    <Label>load</Label>
                    <div className="live-num mt-1 text-[1.3rem] font-light leading-none text-gray-100">
                        {box.load?.map((value) => value.toFixed(2)).join("  ") ?? "—"}
                    </div>
                </div>
            </footer>
        </div>
    );
}

/** The good case: nothing is happening, and the screen says so beautifully. */
function Idle({ board, counts, last, projects, screenKey, now }) {
    const uptime = board.box.uptimeSeconds;
    const days = Math.floor(uptime / 86400);
    return (
        <div className="live-card flex min-h-0 flex-1 flex-col items-center justify-center gap-[1.6rem] p-[1.6rem]">
            <div className="relative flex aspect-square w-[15rem] items-center justify-center">
                <div className="absolute inset-0 rounded-full border border-emerald-400/20" />
                <div className="absolute inset-[18%] rounded-full border border-emerald-400/15" />
                <div className="absolute inset-[36%] rounded-full border border-emerald-400/10" />
                <div className="absolute inset-0 animate-radar rounded-full [background:conic-gradient(from_0deg,rgba(52,211,153,0.35),transparent_28%)]" />
                <Orb tone="up" size="4rem" />
            </div>
            <div className="text-center">
                <div className="text-[2.4rem] font-semibold leading-none tracking-[0.1em] text-emerald-400">ALL SYSTEMS NOMINAL</div>
                <div className="live-label mt-3">
                    {counts.up} of {counts.total} answering · box up {days}d · checked {since(board.status.checkedAt, now)} ago
                </div>
            </div>
            {last ? (
                <div className="live-card flex items-center gap-4 px-[1.2rem] py-[0.9rem]">
                    <Label>last deployment</Label>
                    <span className="font-semibold text-gray-100">{last.slug}</span>
                    <span className="live-num text-gray-500">#{last.number}</span>
                    <span className={toneOf(last.status === "success" ? "up" : "down").text}>{last.status}</span>
                    <span className="live-num text-gray-500">{took(last.durationMs)} · {since(last.endedAt, now)} ago</span>
                </div>
            ) : (
                <Calm title="No deployments yet" />
            )}
        </div>
    );
}
